import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Building2, FileText, Trash2, UserCheck, Users } from "lucide-react";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { api, errorText } from "@/lib/api";

const Card = ({ id, icon: Icon, title, value, note, alert, onClick }) => (
  <button type="button" onClick={onClick} data-testid={`owner-card-${id}`}
    className={`text-left rounded-lg border bg-[#0F141C] p-5 transition-colors hover:bg-[#141A24] ${alert ? "border-amber-500/50" : "border-[#222D3D] hover:border-slate-500"}`}>
    <Icon className={`h-5 w-5 ${alert ? "text-amber-400" : "text-emerald-400"}`} />
    <p className="mt-4 text-sm font-semibold text-slate-100">{title}</p>
    <p className="mt-1 font-display text-2xl text-slate-50" data-testid={`owner-card-${id}-value`}>{value}</p>
    <p className="mt-1 text-xs text-slate-500">{note}</p>
  </button>
);

export const OwnerHome = ({ onOpen }) => {
  const [o, setO] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const load = () => api.get("/admin/overview").then((r) => setO(r.data)).catch((e) => toast.error(errorText(e)));
  useEffect(() => { load(); }, []);

  const cleanup = async () => {
    try { const r = await api.post("/admin/cleanup-test-accounts"); toast.success(`Removed ${r.data.removed} test accounts`); load(); onOpen(null); }
    catch (e) { toast.error(errorText(e)); }
    setConfirm(false);
  };

  if (!o) return null;
  return (
    <section className="space-y-5 fade-up" data-testid="owner-home">
      <div>
        <p className="font-mono text-xs tracking-[0.3em] uppercase text-emerald-400">Owner</p>
        <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-100 mt-2">Owner portal</h1>
        <p className="text-slate-400 text-sm mt-2 max-w-xl">Partners, properties, accounts and your private IP document. Tap a card to open it.</p>
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card id="msps" icon={UserCheck} title="MSP approvals" value={o.pending_msps} note={`waiting · ${o.approved_msps} approved`} alert={o.pending_msps > 0} onClick={() => onOpen("msps")} />
        <Card id="properties" icon={Building2} title="Properties" value={o.properties} note={`${o.awaiting_payment} unpaid · ${o.awaiting_install} to install`} alert={o.awaiting_install > 0} onClick={() => onOpen("properties")} />
        <Card id="accounts" icon={Users} title="Accounts" value={o.accounts} note="everyone who signed up" onClick={() => onOpen("accounts")} />
        <Card id="disclosure" icon={FileText} title="IP document" value="PDF" note="download and timestamp" onClick={() => onOpen("disclosure")} />
      </div>
      {o.test_accounts > 0 && (
        <button type="button" onClick={() => setConfirm(true)} className="flex items-center gap-2 text-sm text-rose-300 hover:text-rose-200" data-testid="owner-cleanup-btn">
          <Trash2 className="h-4 w-4" />Remove {o.test_accounts} test accounts created during testing
        </button>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-[#0F141C] border-[#222D3D]" data-testid="owner-cleanup-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {o.test_accounts} test accounts?</AlertDialogTitle>
            <AlertDialogDescription>Deletes accounts named TEST… or using example.com / t.com / test_ emails, with their ledgers and devices. Your owner account and real sign-ups are kept. This can't be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="owner-cleanup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={cleanup} className="bg-rose-500 hover:bg-rose-400 text-white" data-testid="owner-cleanup-confirm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};
